/** Audit-log list, detail and catalog reads (mgmt API, bearer added by axiosInstance). */
import axiosInstance from 'services/axiosInstance'
import { isAuditServiceError } from 'services/auditLogs/errors'
import {
  AuditLogCatalog,
  AuditLogEvent,
  AuditLogListParams,
  AuditLogListResponse,
  AuditLogQuery,
  AuditLogsApp,
  AuditServiceErrorBody,
} from 'services/auditLogs/types'
import constants from '../../constants'

export const AUDIT_LOGS_PAGE_SIZE = 10

type QueryValue = string | number | string[] | null | undefined

export function getAuditLogsBaseUrl(app: AuditLogsApp): string {
  return `${constants.customerDomain}apps/${app.id}/audit-logs`
}

// A list becomes a repeated key (`section=a&section=b`); empty values are dropped since the API rejects them.
export function buildAuditLogSearchParams(
  values: Record<string, QueryValue>,
): URLSearchParams {
  const params = new URLSearchParams()
  Object.entries(values).forEach(([key, value]) => {
    const items = Array.isArray(value) ? value : [value]
    items.forEach(item => {
      if (item === undefined || item === null || item === '') return
      params.append(key, String(item))
    })
  })
  return params
}

export function toQueryValues(query: AuditLogQuery): Record<string, QueryValue> {
  const { from, to, section, action, actor } = query
  return { from, to, section, action, actor }
}

// Only a real error envelope resolves; a gateway's HTML page or an empty body would otherwise read as
// an empty list or a missing event.
function mapError(error: { response?: { data?: unknown } }) {
  const data = error?.response?.data
  return isAuditServiceError(data) ? data : Promise.reject(error)
}

export async function listAuditLogs(
  app: AuditLogsApp,
  params: AuditLogListParams,
): Promise<AuditLogListResponse | AuditServiceErrorBody> {
  const { cursor, limit = AUDIT_LOGS_PAGE_SIZE, ...query } = params
  const search = buildAuditLogSearchParams({ ...toQueryValues(query), cursor, limit })
  return axiosInstance
    .get(`${getAuditLogsBaseUrl(app)}?${search.toString()}`)
    .then(res => res.data)
    .catch(mapError)
}

export async function getAuditLog(
  app: AuditLogsApp,
  externalId: string,
): Promise<AuditLogEvent | AuditServiceErrorBody> {
  return axiosInstance
    .get(`${getAuditLogsBaseUrl(app)}/${encodeURIComponent(externalId)}`)
    .then(res => res.data)
    .catch(mapError)
}

export async function getAuditLogCatalog(
  app: AuditLogsApp,
): Promise<AuditLogCatalog | AuditServiceErrorBody> {
  return axiosInstance
    .get(`${getAuditLogsBaseUrl(app)}/catalog`)
    .then(res => res.data)
    .catch(mapError)
}
